import React from 'react';
import type { BlurTeaserItem } from '@/store/editorSlice';
import {
  scaledTextRoleStyle,
  type TextRoleStyle,
} from './semanticTextRoles';

/**
 * WEB-926, позиция 2 — blur-тизер с замочками (референс Astroline: стикеры
 * «Совместимость / Большая перемена / Деньги» вокруг результата, значения
 * размыты и помечены замком до оплаты).
 *
 * ГРАНИЦА ОТВЕТСТВЕННОСТИ. Размытие — ОФОРМЛЕНИЕ, а не защита: всё, что автор
 * положил в `teaser`, уходит в разметку страницы открытым текстом и достаётся
 * любым «просмотром кода». Поэтому сюда кладут приманку-обещание
 * («87% совпадения»), а настоящий платный результат отдаётся сервером ПОСЛЕ
 * оплаты и в этот блок не попадает.
 */

/** Сила размытия по умолчанию (px): текст читается как форма, но не как текст. */
export const BLUR_TEASER_DEFAULT_STRENGTH = 6;
export const BLUR_TEASER_MIN_STRENGTH = 1;
export const BLUR_TEASER_MAX_STRENGTH = 32;

interface BlurTeaserBlockProps {
  items: BlurTeaserItem[];
  title?: React.ReactNode;
  note?: string;
  strength?: number;
  layout?: 'grid' | 'list';
  /** Статус размытой карточки для скринридера (локаль воронки). */
  lockedLabel: string;
  /** WEB-1819 п.1: цвет подписи-условия; пусто — прежняя переменная темы. */
  noteColor?: string;
  /** WEB-1819 п.1: кегль подписи-условия в px; пусто — прежний 0.8125em. */
  noteFontSize?: number;
  /** WEB-1819 п.3: своя иконка замочка; пусто — 🔒. */
  lockIconUrl?: string;
  /**
   * WEB-1692 юнит 4: накладка на текст СНАРУЖИ плашек — заголовок над сеткой.
   * Пусто = `{}`, и прежняя строка стиля остаётся байт-в-байт.
   */
  titleRole?: TextRoleStyle;
  /**
   * WEB-1692 юнит 4: накладка на тексты ВНУТРИ плашки. Авторский кегль
   * задаётся по НАЗВАНИЮ строки, приманка и статус считаются от него прежними
   * отношениями (вариант А — решение владельца 02.09 «пропорции система
   * выводит сама»).
   */
  cardRole?: TextRoleStyle;
}

/**
 * Отношения кеглей ВНУТРИ плашки — ровно те, что стоят в вёрстке ниже
 * (0.875em у названия строки, 0.9375em у приманки, 0.75em у статуса). Числа
 * объявлены здесь один раз: вторая копия разъехалась бы при первой правке.
 */
const TEASER_VALUE_RATIO = 0.9375 / 0.875;
const TEASER_STATUS_RATIO = 0.75 / 0.875;

/**
 * WEB-1819 п.2/п.3: картинка-иконка в размер эмодзи. Эмодзи-глиф занимает
 * примерно 1.2em строки — картинка вписывается в тот же квадрат без
 * искажений; у замочка контекст мельче (0.875em), поэтому там квадрат 1em
 * от его собственного размера шрифта.
 */
function iconImageStyle(size: string): React.CSSProperties {
  return {
    width: size,
    height: size,
    objectFit: 'contain',
    verticalAlign: 'middle',
    display: 'inline-block',
  };
}

function clampStrength(value: number | undefined): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return BLUR_TEASER_DEFAULT_STRENGTH;
  return Math.min(BLUR_TEASER_MAX_STRENGTH, Math.max(BLUR_TEASER_MIN_STRENGTH, value));
}

export function BlurTeaserBlock({
  items,
  title,
  note,
  strength,
  layout,
  lockedLabel,
  noteColor,
  noteFontSize,
  lockIconUrl,
  titleRole = {},
  cardRole = {},
}: BlurTeaserBlockProps): React.ReactElement {
  const blur = clampStrength(strength);
  const mode = layout === 'list' ? 'list' : 'grid';
  const valueRole = scaledTextRoleStyle(cardRole, TEASER_VALUE_RATIO);
  const statusRole = scaledTextRoleStyle(cardRole, TEASER_STATUS_RATIO);

  return (
    <div data-testid="blur-teaser" data-layout={mode} style={{ marginBottom: 16 }}>
      {title && (
        <div
          data-testid="blur-teaser-title"
          style={{
            fontSize: '1.125em',
            fontWeight: 600,
            marginBottom: 12,
            textAlign: 'center',
            // WEB-1692 юнит 4: авторский слой ПОСЛЕ прежних строк — пустая
            // накладка ничего не меняет.
            ...titleRole,
          }}
        >
          {title}
        </div>
      )}
      <div
        style={{
          display: 'grid',
          gap: 'var(--option-gap, 12px)',
          // WEB-958: две колонки жёстко, а не auto-fit. Автоподбор требовал
          // 140+12+140 = 292px и схлопывался в одну колонку на ширине превью
          // редактора (≈268px), поэтому «Сетка» выглядела как «Список». Ровно
          // так же (repeat(2, …)) собраны фото-варианты image_choice, и это
          // совпадает с референсом Astroline — стикеры по два в ряд.
          gridTemplateColumns: mode === 'grid' ? 'repeat(2, minmax(0, 1fr))' : '1fr',
        }}
      >
        {items.map((item) => (
          <div
            key={item.id}
            data-testid="blur-teaser-item"
            style={{
              position: 'relative',
              padding: '14px 16px',
              borderRadius: 'var(--radius-card, var(--border-radius-lg, 12px))',
              background: 'var(--color-bg-surface, #fafafa)',
              border: '1px solid var(--color-border-primary, #d9d9d9)',
            }}
          >
            {/* Дефект PM 10.08 (обе поверхности): при длинном заголовке
                замочек «плавал» по вертикали посреди многострочного текста —
                выравнивание по центру строки центрировало его по всей высоте
                переноса. Теперь строка выравнивается по верху: замочек жёстко
                в правом верхнем углу (не сжимается), заголовок переносится
                сам по себе (flex:1 + minWidth:0). */}
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                marginBottom: 6,
              }}
            >
              {item.iconUrl ? (
                <img
                  data-testid="blur-teaser-item-icon"
                  src={item.iconUrl}
                  alt=""
                  aria-hidden="true"
                  style={{ ...iconImageStyle('1.2em'), flexShrink: 0 }}
                />
              ) : (
                item.emoji && (
                  <span aria-hidden="true" style={{ flexShrink: 0 }}>
                    {item.emoji}
                  </span>
                )
              )}
              <span
                data-testid="blur-teaser-item-label"
                style={{
                  fontWeight: 600,
                  fontSize: '0.875em',
                  flex: 1,
                  minWidth: 0,
                  // Живая приёмка WEB-1440: заголовок стикера цвет НЕ задавал и
                  // наследовал его от экрана. На тёмной карточке это совпадало,
                  // а стоило автору залить элемент светлым — заголовок оставался
                  // светлым и пропадал. Цвет на карточке задаётся явно.
                  color: 'var(--color-text-on-surface, var(--color-text-primary, inherit))',
                  // WEB-1692 юнит 4: название строки — главный текст плашки,
                  // от его кегля считаются остальные два.
                  ...cardRole,
                }}
              >
                {item.label}
              </span>
              <span
                data-testid="blur-teaser-lock"
                aria-hidden="true"
                style={{ flexShrink: 0, fontSize: '0.875em' }}
              >
                {lockIconUrl ? (
                  <img src={lockIconUrl} alt="" style={iconImageStyle('1em')} />
                ) : (
                  '🔒'
                )}
              </span>
            </div>
            {/*
              Приманка спрятана от ассистивных технологий намеренно: озвучивать
              её как настоящий ответ — вводить в заблуждение. Вместо неё
              скринридер получает честный статус «скрыто до оплаты».
            */}
            <div
              data-testid="blur-teaser-value"
              aria-hidden="true"
              style={{
                filter: `blur(${blur}px)`,
                userSelect: 'none',
                fontSize: '0.9375em',
                // WEB-1440 (класс WEB-966): текст лежит на СВОЕЙ карточке —
                // цвет обязан идти через on-surface, иначе на тёмной палитре
                // светлый текст ложился на светлую карточку.
                color: 'var(--color-text-on-surface, var(--color-text-secondary, #595959))',
                // WEB-1692 юнит 4: приманка едет от кегля названия строки.
                ...valueRole,
              }}
            >
              {item.teaser}
            </div>
            <span
              data-testid="blur-teaser-locked-status"
              style={{
                display: 'block',
                marginTop: 6,
                fontSize: '0.75em',
                color: 'var(--color-text-on-surface, var(--color-text-tertiary, #8c8c8c))',
                // WEB-1692 юнит 4: статус «скрыто до оплаты» — тоже текст
                // ВНУТРИ плашки, владелец назвал группу целиком.
                ...statusRole,
              }}
            >
              {lockedLabel}
            </span>
          </div>
        ))}
      </div>
      {note && (
        <div
          data-testid="blur-teaser-note"
          style={{
            marginTop: 12,
            textAlign: 'center',
            // WEB-1819 п.1: явный выбор автора сильнее дефолта.
            fontSize: noteFontSize ? `${noteFontSize}px` : '0.8125em',
            // on-surface не нужен: подпись лежит на фоне ЭКРАНА, а не на карточке.
            color: noteColor || 'var(--color-text-secondary, #595959)',
          }}
        >
          {note}
        </div>
      )}
    </div>
  );
}
